/**
 * Offline Reading History Indicator Component
 * Shows offline indicators for cached vs fresh data in reading history
 * Requirements: 9.6, 9.7
 */

import React, { useState } from 'react';
import { 
  Wifi, 
  WifiOff, 
  Database, 
  RefreshCw, 
  AlertTriangle, 
  Clock, 
  HardDrive,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { Modal } from '../common/Modal';

interface OfflineReadingHistoryIndicatorProps {
  isOnline: boolean;
  isUsingCache: boolean;
  isCacheStale: boolean;
  canAccessOffline: boolean;
  accessReason: string;
  cacheMetadata: {
    userId: string;
    readingCount: number;
    lastUpdated: number;
    cacheSize: number;
    isStale: boolean;
  } | null;
  onRefresh?: () => void;
  onClearCache?: () => void;
  isRefreshing?: boolean;
  className?: string;
}

export const OfflineReadingHistoryIndicator: React.FC<OfflineReadingHistoryIndicatorProps> = ({
  isOnline,
  isUsingCache,
  isCacheStale,
  canAccessOffline,
  accessReason,
  cacheMetadata,
  onRefresh,
  onClearCache,
  isRefreshing = false,
  className = ''
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // Determine indicator status and styling
  const getIndicatorStatus = () => {
    if (isOnline) {
      return {
        status: 'online',
        icon: Wifi,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-200 dark:border-green-700',
        message: 'Online - Showing latest readings'
      };
    }

    if (!canAccessOffline) {
      return {
        status: 'offline-no-access',
        icon: XCircle,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        borderColor: 'border-red-200 dark:border-red-700',
        message: accessReason
      };
    }

    if (isCacheStale) {
      return {
        status: 'offline-stale',
        icon: AlertTriangle,
        color: 'text-yellow-600 dark:text-yellow-400',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
        borderColor: 'border-yellow-200 dark:border-yellow-700',
        message: 'Offline - Showing cached readings (may be outdated)'
      };
    }

    return {
      status: 'offline-cached',
      icon: Database,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-700',
      message: 'Offline - Showing cached readings'
    };
  };

  const { status, icon: Icon, color, bgColor, borderColor, message } = getIndicatorStatus();

  // Format cache metadata for display
  const formatCacheInfo = () => {
    if (!cacheMetadata) return null;

    const lastUpdated = new Date(cacheMetadata.lastUpdated);
    const cacheAge = Date.now() - cacheMetadata.lastUpdated;
    const ageInMinutes = Math.floor(cacheAge / (1000 * 60));
    const ageInHours = Math.floor(ageInMinutes / 60);
    const ageInDays = Math.floor(ageInHours / 24);

    let ageText = '';
    if (ageInDays > 0) {
      ageText = `${ageInDays} day${ageInDays !== 1 ? 's' : ''} ago`;
    } else if (ageInHours > 0) {
      ageText = `${ageInHours} hour${ageInHours !== 1 ? 's' : ''} ago`;
    } else if (ageInMinutes > 0) {
      ageText = `${ageInMinutes} minute${ageInMinutes !== 1 ? 's' : ''} ago`;
    } else {
      ageText = 'Just now';
    }

    const sizeInKB = Math.round(cacheMetadata.cacheSize / 1024);

    return {
      readingCount: cacheMetadata.readingCount,
      lastUpdated: lastUpdated.toLocaleString(),
      ageText,
      sizeText: sizeInKB < 1024 ? `${sizeInKB} KB` : `${Math.round(sizeInKB / 1024)} MB`,
      isStale: cacheMetadata.isStale
    };
  };

  const cacheInfo = formatCacheInfo();

  return (
    <>
      <div className={`${bgColor} ${borderColor} border rounded-lg p-3 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon className={`h-4 w-4 ${color}`} />
            <span className={`text-sm font-medium ${color}`}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
            {isUsingCache && cacheInfo && (
              <span className="text-xs text-gray-600 dark:text-gray-400">
                ({cacheInfo.readingCount} cached readings)
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {onRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRefresh}
                disabled={isRefreshing}
                className={`${color} hover:bg-opacity-10`}
              >
                <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(true)}
              className={`${color} hover:bg-opacity-10`}
            >
              <Info className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          {message}
        </p>

        {isUsingCache && cacheInfo && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-500">
            Last updated: {cacheInfo.ageText}
            {isCacheStale && (
              <span className="ml-2 text-yellow-600 dark:text-yellow-400">
                • May be outdated
              </span>
            )}
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Modal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        title="Reading History Status"
      >
        <div className="space-y-4">
          {/* Connection Status */}
          <Card className="p-4">
            <div className="flex items-center space-x-3 mb-3">
              {isOnline ? (
                <Wifi className="h-5 w-5 text-green-500" />
              ) : (
                <WifiOff className="h-5 w-5 text-red-500" />
              )}
              <h3 className="font-semibold text-white">
                Connection Status
              </h3>
            </div>
            <p className="text-gray-400 text-sm">
              {isOnline 
                ? 'You are connected to the internet. Reading history is loaded from the server.'
                : 'You are offline. Reading history is loaded from local cache if available.'
              }
            </p>
          </Card>

          {/* Cache Status */}
          {!isOnline && (
            <Card className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                {canAccessOffline ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <h3 className="font-semibold text-white">
                  Offline Access
                </h3>
              </div>
              <p className="text-gray-400 text-sm mb-3">
                {accessReason}
              </p>

              {cacheInfo && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Cached readings:</span>
                    <span className="text-white">{cacheInfo.readingCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last updated:</span>
                    <span className="text-white">{cacheInfo.lastUpdated}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Cache age:</span>
                    <span className={`${cacheInfo.isStale ? 'text-yellow-400' : 'text-white'}`}>
                      {cacheInfo.ageText}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Cache size:</span>
                    <span className="text-white">{cacheInfo.sizeText}</span>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Data Freshness */}
          <Card className="p-4">
            <div className="flex items-center space-x-3 mb-3">
              <Clock className="h-5 w-5 text-blue-500" />
              <h3 className="font-semibold text-white">
                Data Freshness
              </h3>
            </div>
            <p className="text-gray-400 text-sm">
              {isOnline 
                ? 'Reading history is up-to-date from the server.'
                : isCacheStale
                  ? 'Cached readings may be outdated. Connect to the internet to refresh.'
                  : 'Cached readings are recent and should be accurate.'
              }
            </p>
          </Card>

          {/* Actions */}
          <div className="flex space-x-3">
            {onRefresh && (
              <Button
                onClick={() => {
                  onRefresh();
                  setShowDetails(false);
                }}
                disabled={isRefreshing}
                className="flex-1"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isOnline ? 'Refresh from Server' : 'Reload Cache'}
              </Button>
            )}

            {onClearCache && !isOnline && cacheInfo && (
              <Button
                variant="secondary"
                onClick={() => {
                  onClearCache();
                  setShowDetails(false);
                }}
                className="flex-1"
              >
                <HardDrive className="h-4 w-4 mr-2" />
                Clear Cache
              </Button>
            )}
          </div>

          {/* Tips */}
          <Card className="p-4 bg-blue-900/20 border-blue-700">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-400 mb-2">Tips</h4>
                <ul className="text-sm text-blue-300 space-y-1">
                  <li>• Log in while online to cache your readings for offline access</li>
                  <li>• Cached readings are automatically updated when you go online</li>
                  <li>• Your authentication token must be valid to access cached readings offline</li>
                  {!isOnline && (
                    <li>• Some features like creating new readings require internet connection</li>
                  )}
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </Modal>
    </>
  );
};

export default OfflineReadingHistoryIndicator;